/**
 * 企业微信工具类
 */
import request from './request';

// 声明企业微信 JS-SDK 类型
declare global {
  interface Window {
    wx?: any;
    WeixinJSBridge?: any;
  }
}

/**
 * 判断是否在企业微信环境中
 */
export const isWeComEnvironment = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('wxwork') || ua.includes('micromessenger');
};

interface WeComConfig {
  corpId: string;
  timestamp: number;
  nonceStr: string;
  signature: string;
}

interface AutoLoginResponse {
  success: boolean;
  access_token: string;
  refresh_token: string;
  token_type: string;
  user_info: {
    id: number;
    username: string;
    email: string;
    real_name: string;
    role: string;
    wechat_userid: string;
    wechat_avatar: string;
  };
  message: string;
}

/**
 * 获取企业微信 JS-SDK 配置
 */
const getWeComConfig = async (url: string): Promise<WeComConfig> => {
  try {
    const config: WeComConfig = await request.post('/wechat/jssdk/config', { url });
    return config;
  } catch (error) {
    console.error('获取企业微信配置失败:', error);
    throw error;
  }
};

/**
 * 初始化企业微信 JS-SDK
 */
export const initWeComSDK = async (): Promise<boolean> => {
  try {
    // 判断是否在企业微信环境中
    if (!isWeComEnvironment()) {
      console.log('不在企业微信环境中');
      return false;
    }

    // 等待 wx 对象加载
    if (!window.wx) {
      console.log('企业微信 JS-SDK 未加载');
      return false;
    }

    const wx = window.wx;

    // 获取当前页面 URL（不包含 hash）
    const url = window.location.href.split('#')[0];

    // 获取签名配置
    const config = await getWeComConfig(url);

    console.log('企业微信配置:', config);

    // 配置 JS-SDK
    wx.config({
      beta: true,
      debug: false,
      appId: config.corpId,
      timestamp: config.timestamp,
      nonceStr: config.nonceStr,
      signature: config.signature,
      jsApiList: [
        'getCurExternalContact',
        'getContext',
        'sendChatMessage',
        'openUserProfile',
        'selectEnterpriseContact',
      ],
    });

    return new Promise((resolve) => {
      wx.ready(() => {
        console.log('企业微信 JS-SDK 初始化成功');
        resolve(true);
      });

      wx.error((err: any) => {
        console.error('企业微信 JS-SDK 初始化失败:', err);
        resolve(false);
      });
    });
  } catch (error) {
    console.error('初始化企业微信 JS-SDK 失败:', error);
    return false;
  }
};

/**
 * 获取企业微信用户信息
 */
export const getWeComUserInfo = async (): Promise<any> => {
  if (!window.wx) {
    throw new Error('企业微信 JS-SDK 未初始化');
  }

  return new Promise((resolve, reject) => {
    window.wx.invoke('getContext', {}, (res: any) => {
      if (res.err_msg === 'getContext:ok') {
        console.log('获取企业微信用户信息成功:', res);
        resolve(res);
      } else {
        console.error('获取企业微信用户信息失败:', res);
        reject(res);
      }
    });
  });
};

/**
 * 企业微信自动登录
 */
export const weComAutoLogin = async (): Promise<boolean> => {
  if (!isWeComEnvironment()) {
    console.log('不在企业微信环境中，跳过自动登录');
    return false;
  }

  try {
    // 获取企业微信用户信息
    const userInfo = await getWeComUserInfo();
    console.log('企业微信用户信息:', userInfo);

    // 提取 userid
    const userid = userInfo.entry?.userid || userInfo.userid;
    if (!userid) {
      console.error('未获取到企业微信 userid');
      return false;
    }

    // 调用后端 API 进行自动登录
    const response: AutoLoginResponse = await request.post('/wechat/auto-login', { userid });

    if (response.success && response.access_token) {
      // 保存认证信息
      const { saveAuthData, setUserInfo } = await import('./auth');
      
      saveAuthData({
        access_token: response.access_token,
        refresh_token: response.refresh_token || response.access_token,
        token_type: response.token_type || 'bearer',
      });

      // 保存用户信息
      if (response.user_info) {
        setUserInfo({
          id: response.user_info.id,
          username: response.user_info.username,
          email: response.user_info.email,
          name: response.user_info.real_name,
          role: response.user_info.role as any, // 后端返回的角色字符串
          avatar: response.user_info.wechat_avatar,
          permissions: [],
        });
      }

      console.log('企业微信自动登录成功');
      return true;
    }

    return false;
  } catch (error) {
    console.error('企业微信自动登录失败:', error);
    return false;
  }
};

/**
 * 选择企业微信联系人
 */
export const selectWeComContact = async (): Promise<any[]> => {
  if (!window.wx) {
    throw new Error('企业微信 JS-SDK 未初始化');
  }

  return new Promise((resolve, reject) => {
    window.wx.invoke(
      'selectEnterpriseContact',
      {
        fromDepartmentId: -1, // 从根部门开始
        mode: 'multi', // 多选模式
        type: ['user'], // 只选择用户
      },
      (res: any) => {
        if (res.err_msg === 'selectEnterpriseContact:ok') {
          resolve(res.result.userList || []);
        } else {
          reject(res);
        }
      }
    );
  });
};
